import React, { useEffect, useState } from "react";
import { api, Banner, Button, Field, Modal } from "./ui.tsx";
import {
  emptyMobileRelease,
  type MobileRelease,
  type MobileSettings,
} from "../shared/mobile.ts";

export function MobileSettingsPanel({ onClose }: { onClose: () => void }) {
  const [settings, setSettings] = useState<MobileSettings | null>(null);
  const [value, setValue] = useState<MobileRelease>({ ...emptyMobileRelease });
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [message, setMessage] = useState("");
  const [dirty, setDirty] = useState(false);
  async function load() {
    const result = await api<MobileSettings>("/mobile-settings");
    setSettings(result);
    setValue(
      result.draft?.release ??
        result.published?.release ?? { ...emptyMobileRelease },
    );
    setDirty(false);
  }
  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, []);
  function update<K extends keyof MobileRelease>(
    key: K,
    input: MobileRelease[K],
  ) {
    setValue((v) => ({ ...v, [key]: input }));
    setDirty(true);
    setMessage("");
  }
  async function save(publish: boolean) {
    if (!settings) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await api(
        "/mobile-settings/" + (publish ? "publish" : "draft"),
        publish ? "POST" : "PUT",
        {
          revision: Math.max(
            settings.draft?.revision ?? 0,
            settings.published?.revision ?? 0,
          ),
          release: value,
        },
      );
      await load();
      setMessage(
        publish
          ? "Versão publicada. O Android verificará a política ao consultar o servidor."
          : "Rascunho salvo. A distribuição não mudou.",
      );
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title="Aplicativo e atualizações"
      mobileFullScreen
      onClose={() => {
        if (
          !dirty ||
          window.confirm("Descartar alterações não salvas desta configuração?")
        )
          onClose();
      }}
    >
      <p>
        Android usa APK assinado. iPhone usa navegador e, quando publicado, App
        Store ou TestFlight.
      </p>
      {error && <Banner kind="danger">{error}</Banner>}
      {message && <Banner>{message}</Banner>}
      {!settings ? (
        <Button onClick={() => load().catch((e) => setError(e.message))}>
          Carregar configuração
        </Button>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save(false);
          }}
        >
          <fieldset disabled={busy}>
            <p>
              Publicada: {settings.published?.revision ?? "nenhuma"} · Rascunho:{" "}
              {settings.draft?.revision ?? "novo"}
              {dirty ? " · Alterações não salvas" : ""}
            </p>
            <label>
              <input
                type="checkbox"
                checked={value.enabled}
                onChange={(e) => update("enabled", e.target.checked)}
              />{" "}
              Disponibilizar atualização Android
            </label>
            <Field label="Identificador do aplicativo">
              <input
                required
                value={value.packageId}
                onChange={(e) => update("packageId", e.target.value)}
              />
            </Field>
            <Field label="Nome da versão disponível">
              <input
                required
                value={value.versionName}
                onChange={(e) => update("versionName", e.target.value)}
              />
            </Field>
            <Field label="Código da versão disponível">
              <input
                required
                type="number"
                min="1"
                step="1"
                value={value.versionCode}
                onChange={(e) => update("versionCode", Number(e.target.value))}
              />
            </Field>
            <Field label="Código mínimo aceito">
              <input
                required
                type="number"
                min="1"
                step="1"
                max={value.versionCode}
                value={value.minimumVersionCode}
                onChange={(e) =>
                  update("minimumVersionCode", Number(e.target.value))
                }
              />
            </Field>
            <label>
              <input
                type="checkbox"
                checked={value.mandatory}
                onChange={(e) => update("mandatory", e.target.checked)}
              />{" "}
              Exigir atualização para versões anteriores à disponível
            </label>
            <Field label="Notas da atualização">
              <textarea
                value={value.notes}
                maxLength={6000}
                onChange={(e) => update("notes", e.target.value)}
              />
            </Field>
            <Field label="Endereço HTTPS do APK assinado">
              <input
                type="url"
                required={value.enabled}
                value={value.apkUrl}
                onChange={(e) => update("apkUrl", e.target.value)}
              />
            </Field>
            <Field label="SHA-256 do arquivo">
              <input
                required={value.enabled}
                value={value.sha256}
                onChange={(e) => update("sha256", e.target.value)}
              />
            </Field>
            <Field label="Tamanho exato em bytes">
              <input
                type="number"
                min={value.enabled ? 1 : 0}
                max={314572800}
                required
                value={value.sizeBytes}
                onChange={(e) => update("sizeBytes", Number(e.target.value))}
              />
            </Field>
            <Field label="Android mínimo (nível SDK)">
              <input
                type="number"
                min="26"
                max="100"
                required
                value={value.minSdk}
                onChange={(e) => update("minSdk", Number(e.target.value))}
              />
            </Field>
            <details>
              <summary>Prévia e impacto</summary>
              <p>
                {value.enabled
                  ? `Versão ${value.versionName} (${value.versionCode}), a partir de Android SDK ${value.minSdk}.`
                  : "Nenhuma atualização será oferecida."}{" "}
                {value.mandatory
                  ? "Atualização exigida para versões antigas."
                  : `Abaixo do código ${value.minimumVersionCode}, a atualização será exigida.`}{" "}
                O arquivo será validado pelo aplicativo antes de abrir o
                instalador. Salvar o endereço não comprova hospedagem nem
                assinatura.
              </p>
              <p>
                Não altera usuários, dados, permissões ou recompensas. Restaurar
                política não instala versão anterior no aparelho.
              </p>
            </details>
            <div className="actions">
              <Button type="submit">Salvar rascunho</Button>
              <Button
                kind="primary"
                type="button"
                onClick={(e) => {
                  const form = e.currentTarget.closest("form");
                  if (form?.reportValidity()) save(true);
                }}
              >
                Publicar
              </Button>
            </div>
            <details>
              <summary>Versões e restauração</summary>
              {settings.versions.map((v) => (
                <p key={v.revision}>
                  Configuração {v.revision} · Android {v.release.versionName}{" "}
                  <Button
                    type="button"
                    onClick={() => {
                      setValue(structuredClone(v.release));
                      setDirty(true);
                      setMessage(
                        "Versão carregada para revisão. Publique para aplicar.",
                      );
                    }}
                  >
                    Revisar para restaurar
                  </Button>
                </p>
              ))}
            </details>
          </fieldset>
        </form>
      )}
    </Modal>
  );
}
